import { Injectable } from '@angular/core';
import { Observable, from, of } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { forkJoin } from 'rxjs';
import { LiveFeedRepository } from '../../../domain/live-feed-repository';
import {
  LiveFeed,
  LiveFeedInfo,
  EStreamSourceType,
  StreamSourceSettings,
} from '../../../domain/live-feed';
import { LiveSource, LiveSourceStatus } from '../../../domain/live-sources';

/**
 * Maps EStreamSourceType enum values to human-readable protocol labels.
 * Matches "CHOOSE TYPE" in the live feed create/edit form (scheduler-aside edit).
 */
function getProtocolLabel(sourceType: EStreamSourceType | undefined): string {
  if (sourceType === undefined || sourceType === null) {
    return "—";
  }
  const labels: Record<number, string> = {
    [EStreamSourceType.ZIXI_PUSH]: "ZIXI",
    [EStreamSourceType.ZIXI_PULL]: "ZIXI",
    [EStreamSourceType.RTMP_PUSH]: "RTMP",
    [EStreamSourceType.RTMP_PULL]: "RTMP",
    [EStreamSourceType.HLS_PULL]: "HLS",
    [EStreamSourceType.DASH_PULL]: "DASH",
    [EStreamSourceType.RTSP_PULL]: "RTSP",
    [EStreamSourceType.SRT_PULL]: "SRT",
    [EStreamSourceType.SRT_PUSH]: "SRT",
    [EStreamSourceType.TCP_PULL]: "TCP",
    [EStreamSourceType.TCP_PUSH]: "TCP",
  };
  return labels[sourceType] ?? "—";
}

/**
 * Format remote address and port for display (Address = remoteAddress + remotePort).
 */
function formatAddress(remoteAddress?: string, remotePort?: number): string {
  if (remoteAddress == null || remoteAddress === "") return "—";
  if (remotePort != null && remotePort > 0)
    return `${remoteAddress}:${remotePort}`;
  return remoteAddress;
}

/**
 * Per-platform stream settings live under streamingPlatforms after the yondu
 * live feed model change. For list/detail views use the first platform, matching
 * the live feed editor default (selectedPlatformIndex = 0).
 */
function getPrimaryStreamSourceSettings(
  info: LiveFeedInfo,
): StreamSourceSettings | undefined {
  return info.streamingPlatforms?.[0]?.streamSourceSettings;
}

/**
 * Repository for live sources data.
 * Uses the same data as the scheduler-aside and live feed edit: list from GET /api/livefeeds,
 * details (protocol/source type, address, latency) from GET /api/livefeeds/:id.
 * Latency comes from API TotalLatencyMs; Address from RemoteAddress + RemotePort.
 */
@Injectable({ providedIn: "root" })
export class LiveSourcesRepository {
  constructor(private liveFeedRepository: LiveFeedRepository) {}

  /**
   * Fetch all live sources with full details (protocol, address, latency).
   * Loads the feed list then GET /api/livefeeds/:id for each feed (same as edit window) to get
   * source type (protocol), remote address/port (Address), total latency (Latency).
   */
  getSources(nameFilter?: string): Observable<LiveSource[]> {
    return from(this.liveFeedRepository.getLiveFeeds(nameFilter)).pipe(
      switchMap((feeds: LiveFeed[]) => {
        if (feeds.length === 0) {
          return of([]);
        }
        return forkJoin(
          feeds.map((feed) =>
            from(this.liveFeedRepository.getLiveFeedInfo(feed.id)).pipe(
              map((info: LiveFeedInfo) => this.mapInfoToSource(info)),
              catchError(() => of(this.mapFeedToSource(feed))),
            ),
          ),
        );
      }),
    );
  }

  /**
   * Fetch enriched source details (including protocol, latency, addresses)
   * from the /api/livefeeds/:id endpoint.
   */
  getSourceDetails(id: string): Observable<LiveSource> {
    return from(this.liveFeedRepository.getLiveFeedInfo(id)).pipe(
      map((info: LiveFeedInfo) => this.mapInfoToSource(info)),
    );
  }

  /** Delete a live source by ID. */
  deleteSource(id: string): Observable<void> {
    return from(this.liveFeedRepository.deleteLiveFeed(id));
  }

  /**
   * Map full LiveFeedInfo (from GET /api/livefeeds/:id) to LiveSource.
   * Latency = totalLatencyMs; Address = remoteAddress + remotePort.
   */
  private mapInfoToSource(info: LiveFeedInfo): LiveSource {
    const settings = getPrimaryStreamSourceSettings(info);

    return {
      id: info.id,
      name: info.name,
      status: LiveSourceStatus.Active,
      sourceType: settings?.sourceType,
      protocolLabel: getProtocolLabel(settings?.sourceType),
      thumbnail: info.thumbnail ?? '',
      tenantUniqueId: info.tenantUniqueId,
      remoteAddress: settings?.remoteAddress,
      remotePort: settings?.remotePort,
      streamUrl: settings?.url,
      totalLatencyMs: info.totalLatencyMs,
      address: formatAddress(settings?.remoteAddress, settings?.remotePort),
    };
  }

  /**
   * Map basic LiveFeed (list item) to LiveSource when detail fetch fails.
   */
  private mapFeedToSource(feed: LiveFeed): LiveSource {
    return {
      id: feed.id,
      name: feed.name,
      status: LiveSourceStatus.Active,
      sourceType: undefined,
      protocolLabel: "—",
      thumbnail: feed.thumbnail ?? "",
      tenantUniqueId: feed.tenantUniqueId,
      address: "—",
    };
  }
}
